import { useMemo, useState } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import {
  AlertCircle,
  Archive,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Layers,
  Pencil,
  Plus,
  PlusCircle,
  RefreshCw,
  Search,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { Card, CardContent } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import { useHasAnyPermission, useHasPermission } from '@/hooks/usePermissions';
import { useEquipmentCategories } from '@/hooks/equipment/useEquipment';
import {
  useArchiveEquipmentDescription,
  useEquipmentDescriptions,
} from '@/hooks/equipment/useEquipmentDescriptions';
import { EquipmentDescriptionFormDialog } from '@/components/organisms/equipment/EquipmentDescriptionFormDialog';
import type {
  EquipmentDescription,
  EquipmentDescriptionStatus,
} from '@/types/equipment-description';

export const Route = createFileRoute('/_dashboard/dashboard/equipment-descriptions')({
  beforeLoad: () => {
    const { permissions } = useAuthStore.getState();
    if (permissions.length > 0 && !permissions.includes('equipment:view')) {
      throw redirect({ to: '/dashboard/home' });
    }
  },
  component: EquipmentCatalogPage,
});

const ALL = '__all__';
const PAGE_SIZE = 25;
const STATUS_VALUES: EquipmentDescriptionStatus[] = ['ACTIVE', 'ARCHIVED'];

function EquipmentCatalogPage() {
  const canCreate = useHasPermission('equipment:create');
  const canEdit = useHasPermission('equipment:edit');
  const canDelete = useHasPermission('equipment:delete');
  const canViewPricebookItem = useHasAnyPermission('company:view', 'pricing:view');
  // "Add to pricebook" opens the pricebook create form, which needs the
  // pricebook-create grant — don't link users who can't complete it.
  const canCreatePricebookItem = useHasAnyPermission('company:create', 'pricing:create');

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>(ALL);
  const [status, setStatus] = useState<EquipmentDescriptionStatus>('ACTIVE');
  const [offset, setOffset] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<EquipmentDescription | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<EquipmentDescription | null>(null);

  const categoriesQuery = useEquipmentCategories();
  const archive = useArchiveEquipmentDescription();

  // Resolve a category key → its display name.
  const categoryNameByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categoriesQuery.data ?? []) map.set(c.key, c.name);
    return map;
  }, [categoriesQuery.data]);

  const categoryOptions = (categoriesQuery.data ?? []).filter((c) => c.status !== 'ARCHIVED');

  const filters = useMemo(
    () => ({
      search: search.trim() || undefined,
      category: category === ALL ? undefined : category,
      status,
      limit: PAGE_SIZE,
      offset,
    }),
    [search, category, status, offset],
  );

  const query = useEquipmentDescriptions(filters);
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  function resetPaging() {
    setOffset(0);
  }

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(item: EquipmentDescription) {
    setEditing(item);
    setFormOpen(true);
  }

  function handleFormOpenChange(next: boolean) {
    setFormOpen(next);
    if (!next) setEditing(null);
  }

  function confirmArchive() {
    if (!archiveTarget) return;
    archive.mutate(archiveTarget.id, { onSuccess: () => setArchiveTarget(null) });
  }

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <div className="space-y-6 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Equipment Types</h1>
          <p className="text-sm text-muted-foreground">
            Reusable equipment specs (make/model). Pricebook items link to an equipment type; the
            price lives on the pricebook item.
          </p>
        </div>
        {canCreate ? (
          <Button className="shrink-0" onClick={openCreate}>
            <Plus className="mr-2 size-4" />
            New type
          </Button>
        ) : null}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPaging();
            }}
            placeholder="Search types…"
            className="pl-8"
          />
        </div>
        <Select
          value={category}
          onValueChange={(v) => {
            setCategory(v);
            resetPaging();
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All categories</SelectItem>
            {categoryOptions.map((c) => (
              <SelectItem key={c.id} value={c.key}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v as EquipmentDescriptionStatus);
            resetPaging();
          }}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {value === 'ACTIVE' ? 'Active' : 'Archived'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : query.isError ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <AlertCircle className="size-8 text-destructive" />
              <p className="text-sm text-muted-foreground">Failed to load equipment types.</p>
              <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
                <RefreshCw className="mr-2 size-3.5" />
                Retry
              </Button>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <Layers className="size-8 text-muted-foreground" />
              <p className="text-sm font-medium">No equipment types yet</p>
              <p className="text-sm text-muted-foreground">
                Create a reusable equipment type so pricebook items can link to it.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Pricebook</TableHead>
                  <TableHead>Status</TableHead>
                  {canEdit || canDelete ? (
                    <TableHead className="w-[1%] text-right">Actions</TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-sm font-medium">{item.name}</TableCell>
                    <TableCell className="font-mono text-xs">{item.code}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {categoryNameByKey.get(item.category) ?? item.category}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {item.brand ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {item.model ?? '—'}
                    </TableCell>
                    <TableCell>
                      {item.pricebookItemId ? (
                        canViewPricebookItem ? (
                          <Badge asChild variant="secondary">
                            <Link
                              to="/dashboard/pricebook/$id"
                              params={{ id: item.pricebookItemId }}
                              search={{} as never}
                              aria-label={`Open ${item.name} in the pricebook`}
                              title="Open pricebook item"
                            >
                              In pricebook
                              <ArrowUpRight />
                            </Link>
                          </Badge>
                        ) : (
                          <Badge variant="secondary">In pricebook</Badge>
                        )
                      ) : item.status === 'ACTIVE' && canCreatePricebookItem ? (
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                          className="h-7 border-amber-300 text-amber-700 hover:text-amber-800 dark:border-amber-800 dark:text-amber-400"
                        >
                          <Link
                            to="/dashboard/pricebook/new"
                            search={{ type: 'equipment', equipmentDescriptionId: item.id } as never}
                          >
                            <PlusCircle className="mr-1.5 size-3.5" />
                            Add to pricebook
                          </Link>
                        </Button>
                      ) : (
                        <Badge
                          variant="outline"
                          className="border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-400"
                        >
                          Not in pricebook
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={item.status === 'ACTIVE' ? 'default' : 'secondary'}>
                        {item.status === 'ACTIVE' ? 'Active' : 'Archived'}
                      </Badge>
                    </TableCell>
                    {canEdit || canDelete ? (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {canEdit ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8"
                              onClick={() => openEdit(item)}
                              aria-label="Edit type"
                            >
                              <Pencil className="size-4" />
                            </Button>
                          ) : null}
                          {canDelete && item.status === 'ACTIVE' ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8 text-muted-foreground hover:text-destructive"
                              onClick={() => setArchiveTarget(item)}
                              aria-label="Archive type"
                            >
                              <Archive className="size-4" />
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {pageStart}–{pageEnd} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!canPrev}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            >
              <ChevronLeft className="mr-1 size-4" />
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!canNext}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
            >
              Next
              <ChevronRight className="ml-1 size-4" />
            </Button>
          </div>
        </div>
      ) : null}

      <EquipmentDescriptionFormDialog
        open={formOpen}
        onOpenChange={handleFormOpenChange}
        editing={editing}
      />

      <AlertDialog
        open={archiveTarget != null}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this equipment type?</AlertDialogTitle>
            <AlertDialogDescription>
              {archiveTarget
                ? `"${archiveTarget.name}" will be hidden from the active list. Pricebook items already linked to it are unaffected.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archive.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmArchive();
              }}
              disabled={archive.isPending}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
